import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Sun, Moon, Languages, Heart, ShoppingCart, LogOut, LayoutGrid, Settings } from "lucide-react";

const LOGO = "/lapaz-logo.png";
const LOGO_WHITE = "/lapaz-logo-white.png";

export function Header({ onOpenCart, onOpenFavorites }) {
  const { t, lang, theme, toggleLang, toggleTheme, cartCount, favorites } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");

  const iconBtn =
    "h-12 min-w-[48px] px-3 rounded-xl border bg-card flex items-center justify-center gap-2 hover:bg-muted active:scale-95 transition-colors text-sm font-semibold";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
      <button
        data-testid="header-logo"
        onClick={() => navigate("/catalogo")}
        className="flex items-center gap-3 no-select"
      >
        <img src={LOGO} alt="La Paz Internacional" className="h-9 sm:h-11 w-auto object-contain dark:hidden" />
        <img src={LOGO_WHITE} alt="La Paz Internacional" className="h-9 sm:h-11 w-auto object-contain hidden dark:block" />
        <div className="hidden md:block text-left leading-tight">
          <div className="font-display font-extrabold text-primary text-lg">Appomax</div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{t("company")}</div>
        </div>
      </button>

      <div className="flex items-center gap-2">
        <button data-testid="language-toggle-button" onClick={toggleLang} className={iconBtn}>
          <Languages className="h-5 w-5" />
          <span className="hidden sm:inline">{lang === "es" ? "ES" : "EN"}</span>
        </button>
        <button data-testid="theme-toggle-button" onClick={toggleTheme} className={iconBtn}>
          {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </button>

        {!isAdminPage && (
          <>
            <button data-testid="header-favorites-button" onClick={onOpenFavorites} className={`${iconBtn} relative`}>
              <Heart className="h-5 w-5" />
              {favorites.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                  {favorites.length}
                </span>
              )}
            </button>
            <button data-testid="header-cart-button" onClick={onOpenCart} className={`${iconBtn} relative`}>
              <ShoppingCart className="h-5 w-5" />
              <span className="hidden sm:inline">{t("order")}</span>
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full bg-accent text-accent-foreground text-xs flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </>
        )}

        {user?.role === "admin" && (
          <button
            data-testid="header-admin-toggle"
            onClick={() => navigate(isAdminPage ? "/catalogo" : "/admin")}
            className={iconBtn}
          >
            {isAdminPage ? <LayoutGrid className="h-5 w-5" /> : <Settings className="h-5 w-5" />}
            <span className="hidden lg:inline">{isAdminPage ? t("catalog") : t("admin")}</span>
          </button>
        )}

        <button data-testid="logout-button" onClick={logout} className={iconBtn}>
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}
